import React, { useState, useEffect } from 'react';
import { ProcessStepControl } from '../../services/ProcessStepService';
import { X, Brain, AlertTriangle, CheckCircle, TestTube } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface AddControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (controlData: Partial<ProcessStepControl>) => Promise<void>;
}

export const AddControlModal: React.FC<AddControlModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [formData, setFormData] = useState({
    control_name: '',
    control_description: '',
    control_type: '',
    control_frequency: '',
    control_nature: '',
    automation_level: '',
    control_owner: '',
    control_reviewer: '',

    // Design vs Operating Effectiveness Assessment
    design_effectiveness: '' as 'Effective' | 'Needs Improvement' | 'Ineffective' | '',
    operating_effectiveness: '' as 'Effective' | 'Needs Improvement' | 'Ineffective' | '',
    design_effectiveness_score: 0,
    operating_effectiveness_score: 0,

    // Testing and Validation
    testing_procedure: '',
    test_results: {} as Record<string, any>,
    last_tested_date: '',
    next_test_date: '',
    testing_frequency: '',

    // AI and Recommendations
    recommendations: [] as string[],
    ai_recommended: false,
    ai_confidence_score: 0,
    ai_rationale: '',

    // Gap Management
    identified_gaps: [] as Array<{ description: string; severity: string; remediation: string }>,

    // Compliance and Regulatory
    regulatory_citations: [] as Array<{ regulation: string; reference: string; description?: string }>,

    // Implementation and Status
    implementation_status: 'implemented' as string,
    manual_override_reason: '',
    residual_risk_manual: false
  });

  const [newCitation, setNewCitation] = useState({
    regulation: '',
    reference: '',
    description: ''
  });

  const [newGap, setNewGap] = useState({
    description: '',
    severity: 'Medium',
    remediation: ''
  });

  const [isGeneratingTestProcedure, setIsGeneratingTestProcedure] = useState(false);
  const [activeTab, setActiveTab] = useState('basic'); // basic, effectiveness, testing, gaps

  // Reset loading states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsLoadingAI(false);
      setIsGeneratingTestProcedure(false);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addRegulatoryCitation = () => {
    if (newCitation.regulation && newCitation.reference) {
      setFormData(prev => ({
        ...prev,
        regulatory_citations: [...prev.regulatory_citations, { ...newCitation }]
      }));
      setNewCitation({ regulation: '', reference: '', description: '' });
    }
  };

  const removeCitation = (index: number) => {
    setFormData(prev => ({
      ...prev,
      regulatory_citations: prev.regulatory_citations.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const controlData = {
        ...formData,
        control_id: `CTRL-${Date.now()}`, // Generate a unique control ID
        last_tested_date: formData.last_tested_date || undefined,
        next_test_date: formData.next_test_date || undefined
      };
      await onSave(controlData);
      onClose();
      // Reset form
      setFormData({
        control_name: '',
        control_description: '',
        control_type: '',
        control_frequency: '',
        control_nature: '',
        automation_level: '',
        control_owner: '',
        control_reviewer: '',
        design_effectiveness: '',
        operating_effectiveness: '',
        design_effectiveness_score: 0,
        operating_effectiveness_score: 0,
        testing_procedure: '',
        test_results: {},
        last_tested_date: '',
        next_test_date: '',
        testing_frequency: '',
        recommendations: [],
        ai_recommended: false,
        ai_confidence_score: 0,
        ai_rationale: '',
        identified_gaps: [],
        regulatory_citations: [],
        implementation_status: 'implemented',
        manual_override_reason: '',
        residual_risk_manual: false
      });
    } catch (error) {
      console.error('Failed to add control:', error);
    }
  };

  const suggestAIControl = async () => {
    if (isLoadingAI) return;

    const loadingToast = toast.loading('AI is generating control suggestions... This may take up to 30 seconds.');
    setIsLoadingAI(true);

    try {
      const response = await api.post('/api/ai/generate-control-suggestions', {
        control_type: formData.control_type,
        control_frequency: formData.control_frequency,
        control_nature: formData.control_nature,
        existing_description: formData.control_description
      });

      if (response.data && response.data.suggestions && response.data.suggestions.length > 0) {
        const suggestion = response.data.suggestions[0];

        setFormData(prev => ({
          ...prev,
          control_name: suggestion.control_name || prev.control_name,
          control_description: suggestion.control_description || prev.control_description,
          testing_procedure: suggestion.testing_procedure || prev.testing_procedure,
          recommendations: suggestion.recommendations || prev.recommendations,
          ai_recommended: true,
          ai_confidence_score: suggestion.confidence_score || 85,
          ai_rationale: suggestion.rationale || 'AI-generated control based on best practices'
        }));

        toast.dismiss(loadingToast);
        toast.success('AI control suggestions generated successfully!');
      } else {
        toast.dismiss(loadingToast);
        throw new Error('No AI suggestions returned');
      }
    } catch (error: any) {
      console.error('Failed to generate AI control suggestions:', error);
      toast.dismiss(loadingToast);

      if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
        toast.error('Request timed out. The AI service might be busy. Please try again.', { duration: 5000 });
      } else {
        toast.error(`Failed to generate AI suggestions: ${error.response?.data?.message || error.message || 'Unknown error'}`, { duration: 5000 });
      }
    } finally {
      setIsLoadingAI(false);
    }
  };

  const generateTestingProcedure = async () => {
    if (isGeneratingTestProcedure) return;

    setIsGeneratingTestProcedure(true);
    try {
      const response = await api.post('/api/ai/generate-testing-procedure', {
        control_name: formData.control_name,
        control_description: formData.control_description,
        control_type: formData.control_type,
        control_frequency: formData.control_frequency
      });

      if (response.data && response.data.procedure) {
        setFormData(prev => ({
          ...prev,
          testing_procedure: response.data.procedure,
          recommendations: [...prev.recommendations, ...response.data.recommendations || []]
        }));
      }
    } catch (error) {
      console.error('Failed to generate testing procedure:', error);
      setFormData(prev => ({
        ...prev,
        testing_procedure: `Recommended testing procedure for ${formData.control_name}:\n\n1. Review control design adequacy\n2. Test control operation over the period\n3. Evaluate control effectiveness\n4. Document findings and exceptions\n5. Report results to management`,
        recommendations: [...prev.recommendations, 'Regular testing schedule recommended', 'Document all test results']
      }));
    } finally {
      setIsGeneratingTestProcedure(false);
    }
  };

  const addControlGap = () => {
    if (newGap.description) {
      setFormData(prev => ({
        ...prev,
        identified_gaps: [...prev.identified_gaps, { ...newGap }]
      }));
      setNewGap({ description: '', severity: 'Medium', remediation: '' });
    }
  };

  const removeGap = (index: number) => {
    setFormData(prev => ({
      ...prev,
      identified_gaps: prev.identified_gaps.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-[#221551] text-white">
          <h2 className="text-xl font-semibold">Add Control</h2>
          <div className="flex items-center gap-4">
            {/* AI Suggestion Button - MOVED OUTSIDE FORM */}
            <button
              type="button"
              onClick={suggestAIControl}
              disabled={isLoadingAI}
              className="flex items-center gap-2 px-4 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <Brain className={`h-4 w-4 ${isLoadingAI ? 'animate-spin' : ''}`} />
              {isLoadingAI ? 'Generating...' : 'AI Suggest Control'}
            </button>

            {/* Testing Procedure Button - Show only on Testing tab */}
            {activeTab === 'testing' && (
              <button
                type="button"
                onClick={generateTestingProcedure}
                disabled={isGeneratingTestProcedure}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <Brain className={`h-4 w-4 ${isGeneratingTestProcedure ? 'animate-spin' : ''}`} />
                {isGeneratingTestProcedure ? 'Generating...' : 'AI Generate Procedure'}
              </button>
            )}

            <button onClick={onClose} className="text-white hover:text-gray-300">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Tab Navigation */}
          <div className="border-b bg-gray-50">
            <div className="flex">
              {[
                { id: 'basic', label: 'Basic Info', icon: '📋' },
                { id: 'effectiveness', label: 'Effectiveness', icon: '⚡' },
                { id: 'testing', label: 'Testing', icon: '🧪' },
                { id: 'gaps', label: 'Gaps & Issues', icon: '⚠️' }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-b-2 border-[#57C8F4] text-[#57C8F4] bg-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6 space-y-6">

            {/* Tab Content */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                {/* Control Name and Description */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Control Name *
                    </label>
                    <input
                      type="text"
                      value={formData.control_name}
                      onChange={(e) => handleInputChange('control_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Control Description
                    </label>
                    <textarea
                      value={formData.control_description}
                      onChange={(e) => handleInputChange('control_description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                  </div>
                </div>

                {/* Control Characteristics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Control Type
                    </label>
                    <select
                      value={formData.control_type}
                      onChange={(e) => handleInputChange('control_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    >
                      <option value="">Select Type</option>
                      <option value="Preventive">Preventive</option>
                      <option value="Detective">Detective</option>
                      <option value="Corrective">Corrective</option>
                      <option value="Compensating">Compensating</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Control Frequency
                    </label>
                    <select
                      value={formData.control_frequency}
                      onChange={(e) => handleInputChange('control_frequency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    >
                      <option value="">Select Frequency</option>
                      <option value="Real-time">Real-time</option>
                      <option value="Per Transaction">Per Transaction</option>
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Annual">Annual</option>
                      <option value="As Needed">As Needed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Control Nature
                    </label>
                    <select
                      value={formData.control_nature}
                      onChange={(e) => handleInputChange('control_nature', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    >
                      <option value="">Select Nature</option>
                      <option value="Automated">Automated</option>
                      <option value="Manual">Manual</option>
                      <option value="Semi-automated">Semi-automated</option>
                    </select>
                  </div>
                </div>

                {/* Ownership */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Control Owner
                    </label>
                    <input
                      type="text"
                      value={formData.control_owner}
                      onChange={(e) => handleInputChange('control_owner', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      placeholder="Person/Department responsible"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Control Reviewer
                    </label>
                    <input
                      type="text"
                      value={formData.control_reviewer}
                      onChange={(e) => handleInputChange('control_reviewer', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      placeholder="Person who reviews the control"
                    />
                  </div>
                </div>

                {/* Regulatory Citations */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Regulatory Citations
                  </label>

                  {/* Existing Citations */}
                  {formData.regulatory_citations.map((citation, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-md mb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-[#221551]">
                            {citation.regulation}
                          </div>
                          <div className="text-sm text-[#454F83]">
                            {citation.reference}
                          </div>
                          {citation.description && (
                            <div className="text-xs text-gray-600 mt-1">
                              {citation.description}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCitation(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add New Citation */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3 border border-dashed border-gray-300 rounded-md">
                    <input
                      type="text"
                      placeholder="Regulation (e.g., SOX Section 404)"
                      value={newCitation.regulation}
                      onChange={(e) => setNewCitation(prev => ({ ...prev, regulation: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                    <input
                      type="text"
                      placeholder="Reference (e.g., 15 USC § 7262)"
                      value={newCitation.reference}
                      onChange={(e) => setNewCitation(prev => ({ ...prev, reference: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Description (optional)"
                        value={newCitation.description}
                        onChange={(e) => setNewCitation(prev => ({ ...prev, description: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      />
                      <button
                        type="button"
                        onClick={addRegulatoryCitation}
                        className="px-3 py-2 bg-[#30A8CF] text-white rounded-md hover:bg-[#57C8F4]"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* AI Fields */}
                <div className="border-t pt-4">
                  <label className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={formData.ai_recommended}
                      onChange={(e) => handleInputChange('ai_recommended', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">AI Recommended Control</span>
                  </label>

                  {formData.ai_recommended && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          AI Confidence Score (0-100)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.ai_confidence_score}
                          onChange={(e) => handleInputChange('ai_confidence_score', parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          AI Rationale
                        </label>
                        <textarea
                          value={formData.ai_rationale}
                          onChange={(e) => handleInputChange('ai_rationale', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                          placeholder="Explanation of AI reasoning"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Effectiveness Tab */}
            {activeTab === 'effectiveness' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <span className="mr-2">⚡</span>
                    Control Effectiveness Assessment
                  </h3>
                  <p className="text-sm text-blue-800">
                    Assess both design and operating effectiveness separately to get a comprehensive view of control performance.
                  </p>
                </div>

                {/* Design Effectiveness */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Design Effectiveness
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Design Effectiveness Rating
                      </label>
                      <select
                        value={formData.design_effectiveness}
                        onChange={(e) => handleInputChange('design_effectiveness', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      >
                        <option value="">Select Rating</option>
                        <option value="Effective">Effective</option>
                        <option value="Needs Improvement">Needs Improvement</option>
                        <option value="Ineffective">Ineffective</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Design Effectiveness Score (0-100)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.design_effectiveness_score}
                        onChange={(e) => handleInputChange('design_effectiveness_score', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      />
                    </div>
                  </div>
                </div>

                {/* Operating Effectiveness */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-3 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    Operating Effectiveness
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Operating Effectiveness Rating
                      </label>
                      <select
                        value={formData.operating_effectiveness}
                        onChange={(e) => handleInputChange('operating_effectiveness', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      >
                        <option value="">Select Rating</option>
                        <option value="Effective">Effective</option>
                        <option value="Needs Improvement">Needs Improvement</option>
                        <option value="Ineffective">Ineffective</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Operating Effectiveness Score (0-100)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.operating_effectiveness_score}
                        onChange={(e) => handleInputChange('operating_effectiveness_score', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      />
                    </div>
                  </div>
                </div>

                {/* AI Recommendations */}
                {formData.recommendations.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-3 flex items-center">
                      <Brain className="h-5 w-5 mr-2" />
                      AI Recommendations
                    </h4>
                    <ul className="space-y-2">
                      {formData.recommendations.map((rec, index) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-2 mt-0.5">•</span>
                          <span className="text-sm text-purple-800">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Manual Override */}
                <div className="border-t pt-4">
                  <label className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={formData.residual_risk_manual}
                      onChange={(e) => handleInputChange('residual_risk_manual', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Manual Residual Risk Override</span>
                  </label>

                  {formData.residual_risk_manual && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Override Reason
                      </label>
                      <textarea
                        value={formData.manual_override_reason}
                        onChange={(e) => handleInputChange('manual_override_reason', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                        placeholder="Explain why manual override is necessary..."
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Testing Tab */}
            {activeTab === 'testing' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <TestTube className="h-5 w-5 mr-2" />
                    Control Testing Configuration
                  </h3>
                  <p className="text-sm text-blue-800">
                    Define testing procedures and schedules for this control.
                  </p>
                </div>

                {/* Testing Procedure */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Testing Procedure
                  </label>
                  <textarea
                    value={formData.testing_procedure}
                    onChange={(e) => handleInputChange('testing_procedure', e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="Detail the step-by-step testing procedure for this control..."
                  />
                </div>

                {/* Testing Schedule */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Testing Frequency
                    </label>
                    <select
                      value={formData.testing_frequency}
                      onChange={(e) => handleInputChange('testing_frequency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    >
                      <option value="">Select Frequency</option>
                      <option value="Continuous">Continuous</option>
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Semi-Annually">Semi-Annually</option>
                      <option value="Annually">Annually</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Tested Date
                    </label>
                    <input
                      type="date"
                      value={formData.last_tested_date}
                      onChange={(e) => handleInputChange('last_tested_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Next Test Date
                    </label>
                    <input
                      type="date"
                      value={formData.next_test_date}
                      onChange={(e) => handleInputChange('next_test_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                  </div>
                </div>

                {/* Implementation Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Implementation Status
                  </label>
                  <select
                    value={formData.implementation_status}
                    onChange={(e) => handleInputChange('implementation_status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="implemented">Implemented</option>
                    <option value="testing">Testing</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="retired">Retired</option>
                  </select>
                </div>
              </div>
            )}

            {/* Gaps Tab */}
            {activeTab === 'gaps' && (
              <div className="space-y-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-semibold text-red-900 mb-2 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    Control Gap Management
                  </h3>
                  <p className="text-sm text-red-800">
                    Identify and track gaps in control design or operation that need remediation.
                  </p>
                </div>

                {/* Existing Gaps */}
                {formData.identified_gaps.map((gap, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            gap.severity === 'High' ? 'bg-red-100 text-red-800' :
                            gap.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {gap.severity} Severity
                          </span>
                        </div>
                        <div className="font-medium text-gray-900 mb-2">
                          {gap.description}
                        </div>
                        <div className="text-sm text-gray-600">
                          <strong>Remediation:</strong> {gap.remediation}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeGap(index)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Add New Gap */}
                <div className="border border-dashed border-gray-300 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Add New Gap</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gap Description
                      </label>
                      <textarea
                        value={newGap.description}
                        onChange={(e) => setNewGap(prev => ({ ...prev, description: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                        placeholder="Describe the identified gap..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Severity Level
                        </label>
                        <select
                          value={newGap.severity}
                          onChange={(e) => setNewGap(prev => ({ ...prev, severity: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                        </select>
                      </div>

                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={addControlGap}
                          className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        >
                          Add Gap
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Remediation Plan
                      </label>
                      <textarea
                        value={newGap.remediation}
                        onChange={(e) => setNewGap(prev => ({ ...prev, remediation: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                        placeholder="Describe the remediation plan..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-4 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors"
            >
              Add Control
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};